import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fmtError, TERRITOIRES } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { HardHat, MapPin, Plus, Crosshair } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { nom: "", client_nom: "", territoire: "Guadeloupe", commune: "", adresse: "",
  reference_cadastrale: "", type_batiment: "", type_travaux: "", neuf_renovation: "Rénovation",
  erp: false, habitation: true, statut: "À démarrer", date_debut: "", responsable: "", notes: "",
  latitude: null, longitude: null };

const STATUT_COLORS = {
  "En cours": "bg-emerald-500", "À démarrer": "bg-sky-500", "Prospection": "bg-slate-400",
  "En pause": "bg-amber-500", "Terminé": "bg-slate-600", "Réception": "bg-emerald-700", "Archivé": "bg-slate-400",
};

export default function Sites() {
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/sites").then((r) => setSites(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { if (params.get("new")) { setOpen(true); setParams({}, { replace: true }); } }, [params, setParams]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const geolocate = () => {
    navigator.geolocation?.getCurrentPosition(
      (pos) => { set("latitude", pos.coords.latitude); set("longitude", pos.coords.longitude); toast.success("Position GPS enregistrée"); },
      () => toast.error("Géolocalisation refusée ou indisponible"), { enableHighAccuracy: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/sites", form);
      toast.success("Chantier créé");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) { toast.error(fmtError(err)); }
  };

  return (
    <div className="space-y-5" data-testid="sites-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight">Chantiers</h1>
        <Button data-testid="new-site-button" onClick={() => setOpen(true)} className="bg-amber-500 text-slate-900 font-bold hover:bg-amber-400 h-12">
          <Plus className="h-5 w-5 mr-1" /> Nouveau
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="sites-list">
        {sites.map((s) => (
          <Link key={s.id} to={`/chantiers/${s.id}`} data-testid={`site-card-${s.id}`}
            className="bg-card rounded-xl border-2 border-border hover:border-amber-500 p-4 transition-all hover:shadow-md">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-display font-bold text-lg leading-tight">{s.nom}</div>
                <div className="text-xs text-muted-foreground font-mono-num">{s.numero}</div>
              </div>
              <span className={`${STATUT_COLORS[s.statut] || "bg-slate-400"} text-white text-[10px] font-bold px-2 py-1 rounded-md uppercase`}>{s.statut}</span>
            </div>
            <div className="mt-2 text-sm text-muted-foreground flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" /> {s.commune || "—"} • {s.territoire}
            </div>
            {s.client_nom && <div className="text-sm font-semibold mt-1">{s.client_nom}</div>}
          </Link>
        ))}
        {sites.length === 0 && <p className="text-muted-foreground text-sm col-span-full" data-testid="sites-empty">Aucun chantier. Créez le premier.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="site-form-modal">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Nouveau chantier</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <Input data-testid="site-nom" required placeholder="Nom du chantier *" value={form.nom} onChange={(e) => set("nom", e.target.value)} className="py-3.5" />
            <Input data-testid="site-client" placeholder="Client" value={form.client_nom} onChange={(e) => set("client_nom", e.target.value)} />
            <div className="grid grid-cols-2 gap-2">
              <select data-testid="site-territoire" value={form.territoire} onChange={(e) => set("territoire", e.target.value)} className="h-12 rounded-lg border border-input bg-background px-3 text-sm font-semibold">
                {TERRITOIRES.map((t) => <option key={t}>{t}</option>)}
              </select>
              <Input data-testid="site-commune" placeholder="Commune" value={form.commune} onChange={(e) => set("commune", e.target.value)} />
            </div>
            <Input data-testid="site-adresse" placeholder="Adresse" value={form.adresse} onChange={(e) => set("adresse", e.target.value)} />
            <div className="flex gap-2">
              <Button type="button" data-testid="site-geolocate" onClick={geolocate} variant="outline" className="h-12 font-bold flex-1">
                <Crosshair className="h-4 w-4 mr-2" /> GPS
              </Button>
              <div className="flex-1 text-xs font-mono-num self-center text-muted-foreground" data-testid="site-gps-display">
                {form.latitude ? `${form.latitude.toFixed(5)}, ${form.longitude.toFixed(5)}` : "Non localisé"}
              </div>
            </div>
            <Input data-testid="site-cadastre" placeholder="Référence cadastrale (si connue)" value={form.reference_cadastrale} onChange={(e) => set("reference_cadastrale", e.target.value)} />
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="site-type-batiment" placeholder="Type de bâtiment" value={form.type_batiment} onChange={(e) => set("type_batiment", e.target.value)} />
              <Input data-testid="site-type-travaux" placeholder="Type de travaux" value={form.type_travaux} onChange={(e) => set("type_travaux", e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <select data-testid="site-neuf-reno" value={form.neuf_renovation} onChange={(e) => set("neuf_renovation", e.target.value)} className="h-12 rounded-lg border border-input bg-background px-3 text-sm font-semibold">
                <option>Neuf</option><option>Rénovation</option>
              </select>
              <select data-testid="site-statut" value={form.statut} onChange={(e) => set("statut", e.target.value)} className="h-12 rounded-lg border border-input bg-background px-3 text-sm font-semibold">
                {Object.keys(STATUT_COLORS).map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="flex gap-4 text-sm font-semibold">
              <label className="flex items-center gap-2"><input type="checkbox" data-testid="site-erp" checked={form.erp} onChange={(e) => set("erp", e.target.checked)} /> ERP</label>
              <label className="flex items-center gap-2"><input type="checkbox" data-testid="site-habitation" checked={form.habitation} onChange={(e) => set("habitation", e.target.checked)} /> Habitation</label>
            </div>
            <Input data-testid="site-responsable" placeholder="Conducteur / responsable" value={form.responsable} onChange={(e) => set("responsable", e.target.value)} />
            <Button data-testid="site-submit" type="submit" className="w-full h-14 bg-amber-500 text-slate-900 font-bold hover:bg-amber-400">
              <HardHat className="h-5 w-5 mr-2" /> CRÉER LE CHANTIER
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
